import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Envelope } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

export default function PrivacyPage() {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Information We Collect",
      content: "We may collect the following types of information:",
      subsections: [
        {
          subtitle: "a. Personal Information",
          items: ["Name", "Email address", "Phone number"]
        },
        {
          subtitle: "b. Usage Information",
          items: ["Pages visited", "Actions taken on the platform (such as registrations or bookings)"]
        },
        {
          subtitle: "c. Trainer Information",
          items: ["Professional details shared by trainers", "Payment or contact details provided for listing masterclasses"]
        }
      ]
    },
    {
      title: "2. How We Use Your Information",
      content: "We use your information to:",
      items: [
        "Facilitate bookings between learners and trainers",
        "Share relevant details with trainers for session coordination",
        "Improve platform experience and features",
        "Communicate updates, confirmations, or support messages"
      ]
    },
    {
      title: "3. Information Sharing",
      items: [
        "We may share necessary user details with trainers for the purpose of conducting sessions.",
        "We do not sell your personal information to third parties.",
        "We may share information if required by law or to protect our rights."
      ]
    },
    {
      title: "4. Payments",
      items: [
        "In the current phase, payments may be handled directly by trainers.",
        "We do not store or process your payment details on our platform unless explicitly stated."
      ]
    },
    {
      title: "5. Data Security",
      content: "We take reasonable steps to protect your information from unauthorized access, misuse, or loss. However, no system is completely secure."
    },
    {
      title: "6. Data Retention",
      content: "We retain your information only as long as necessary to provide our services or comply with legal obligations."
    },
    {
      title: "7. Your Rights",
      content: "You may:",
      items: [
        "Request access to your personal data",
        "Ask for corrections or updates",
        "Request deletion of your data"
      ],
      footer: "To make such requests, contact us using the details below."
    },
    {
      title: "8. Cookies and Tracking",
      content: "We may use basic cookies or similar technologies to improve user experience and analyze usage patterns."
    },
    {
      title: "9. Third-Party Links",
      content: "Our platform may contain links to external websites or trainer pages. We are not responsible for their privacy practices."
    },
    {
      title: "10. Updates to This Policy",
      content: "We may update this Privacy Policy from time to time. Continued use of the platform implies acceptance of the updated policy."
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div
            onClick={() => navigate('/')}
            className="text-2xl font-bold tracking-tight cursor-pointer text-white flex items-center gap-0"
            data-testid="logo"
          >
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </div>
          <div className="flex gap-6 items-center">
            <button onClick={() => navigate('/marketplace')} className="text-white/90 hover:text-white transition-colors">
              Browse
            </button>
            <button onClick={() => navigate('/about')} className="text-white/90 hover:text-white transition-colors">
              About
            </button>
            <button onClick={() => navigate('/')} className="text-white/90 hover:text-white transition-colors">
              Home
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 gradient-header">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <Shield size={32} className="text-white" weight="duotone" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 text-white" data-testid="privacy-title">
              Privacy Policy
            </h1>
            <p className="text-xl text-white/90">
              We value your privacy and are committed to protecting your personal information
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-lg text-gray-700 leading-relaxed">
              This Privacy Policy explains how we collect, use, and safeguard your data when you use our platform.
            </p>
          </motion.div>

          {/* Sections */}
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-50 rounded-xl p-8 border border-gray-200"
              >
                <h2 className="text-2xl font-bold mb-4 text-gray-900">{section.title}</h2>
                
                {section.content && (
                  <p className="text-gray-700 mb-4">{section.content}</p>
                )}

                {section.subsections && (
                  <div className="space-y-4">
                    {section.subsections.map((sub, subIndex) => (
                      <div key={subIndex}>
                        <h3 className="font-semibold text-gray-900 mb-2">{sub.subtitle}</h3>
                        <ul className="space-y-2">
                          {sub.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start text-gray-700">
                              <span className="text-[#00a3af] mr-2">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {section.items && !section.subsections && (
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start text-gray-700">
                        <span className="text-[#00a3af] mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.footer && (
                  <p className="text-gray-700 mt-4 italic">{section.footer}</p>
                )}
              </motion.div>
            ))}

            {/* Contact Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#00a3af]/10 to-[#34c759]/10 rounded-xl p-8 border border-[#00a3af]/20"
            >
              <h2 className="text-2xl font-bold mb-4 text-gray-900">11. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions or concerns about this Privacy Policy, please contact us at:
              </p>
              <a 
                href="mailto:contact@neomaestro.in"
                className="inline-flex items-center gap-2 text-[#00a3af] hover:text-[#004d7a] font-semibold transition-colors"
              >
                <Envelope size={24} weight="duotone" />
                contact@neomaestro.in
              </a>
            </motion.div>

            {/* Agreement Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#004d7a]/5 rounded-xl p-6 border border-[#004d7a]/20 text-center"
            >
              <p className="text-gray-800 font-medium">
                By using our platform, you agree to the terms outlined in this Privacy Policy.
              </p>
            </motion.div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/marketplace')}
              className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-12 py-6 text-lg font-bold"
            >
              Start Exploring
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="gradient-footer text-white py-12 px-6 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold mb-4">Company</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Resources</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors">Browse Courses</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Legal</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Get Started</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/auth?role=instructor')} className="hover:text-white transition-colors">Teach a Masterclass</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-white/70 text-sm">© 2026 NeoMaestro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
